/**
 * CorrectSettlementDialog (Iter 60)
 *
 * Primary-Admin-only **data-entry correction** dialog for a recorded
 * settlement. Lets the operator edit BOTH cash ``amount`` and
 * ``tds_amount`` on the SAME payment row in a single audited action.
 *
 * This is deliberately NOT the routine "Adjust TDS" flow. Framing,
 * copy, and visual treatment are all designed to communicate that this
 * exists to fix recording mistakes, not for casual edits.
 *
 * Backend: PATCH /api/billing/settlement/{payment_id}/correct
 *   body: { amount, tds_amount, reason, note }
 *
 * Invariants enforced server-side (mirrored client-side for UX clarity):
 *   - amount >= 0 AND tds_amount >= 0
 *   - amount + tds_amount > 0 (composite must be a positive economic event)
 *   - paid_other + amount + tds_other + tds_amount <= effective_total + 0.01
 *
 * Identity-preserving — payment_id, payment_date, payment_mode, etc.
 * are never touched. Existing TDS lifecycle stage is preserved.
 */
import { useEffect, useMemo, useState } from "react";
import { Pencil, Warning, ShieldCheck, ArrowRight } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatINR } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { DialogFooter } from "./ui/dialog";
import FormDialog, { FormDialogCancel } from "./FormDialog";
import { toast } from "sonner";

const REASONS = [
    { value: "DataEntryError",  label: "Data-entry error — wrong figure typed" },
    { value: "WrongAmount",     label: "Wrong amount — inconsistent with bank statement" },
    { value: "DuplicateAttempt", label: "Duplicate attempt — netting out a stray capture" },
    { value: "Other",           label: "Other (must be substantively explained in the note)" },
];

const NOTE_MIN = 5;

export default function CorrectSettlementDialog({
    open, onOpenChange, payment, invoice, onDone,
}) {
    const [amount, setAmount] = useState("");
    const [tdsAmount, setTdsAmount] = useState("");
    const [reason, setReason] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    // Iter 61.2 — retrofit cross-currency receipt fields on this row.
    const [receiptEnabled, setReceiptEnabled] = useState(false);
    const [receiptCurrency, setReceiptCurrency] = useState("INR");
    const [receiptAmount, setReceiptAmount] = useState("");
    const [clearReceipt, setClearReceipt] = useState(false);

    const invoiceCurrency = (invoice?.currency || "INR").toUpperCase();

    // Reset every time we open onto a new payment
    useEffect(() => {
        if (!open) {
            setAmount(""); setTdsAmount(""); setReason(""); setNote(""); setBusy(false);
            setReceiptEnabled(false); setReceiptCurrency("INR"); setReceiptAmount("");
            setClearReceipt(false);
            return;
        }
        setAmount(payment?.amount != null ? String(payment.amount) : "0");
        setTdsAmount(payment?.tds_amount != null ? String(payment.tds_amount) : "0");
        setReason("");
        setNote("");
        // Iter 61.2 — pre-populate receipt fields if the row already has them.
        if (payment?.receipt_currency && payment?.receipt_amount) {
            setReceiptEnabled(true);
            setReceiptCurrency(payment.receipt_currency);
            setReceiptAmount(String(payment.receipt_amount));
        } else {
            setReceiptEnabled(false);
            // Default the dropdown to the FIRST currency that differs from
            // the invoice currency — so when the toggle flips on, the
            // dropdown's visible value and the form state agree from the start.
            const firstAlt = ["INR", "USD", "AED", "GBP", "EUR"]
                .find((c) => c !== invoiceCurrency) || "INR";
            setReceiptCurrency(firstAlt);
            setReceiptAmount("");
        }
        setClearReceipt(false);
    }, [open, payment?.id, payment?.amount, payment?.tds_amount,
        payment?.receipt_currency, payment?.receipt_amount, invoiceCurrency]);

    const oldAmount = Number(payment?.amount || 0);
    const oldTds = Number(payment?.tds_amount || 0);
    const newAmount = Number(amount || 0);
    const newTds = Number(tdsAmount || 0);

    // Iter 61.2 — receipt-field state diff vs. row's current values.
    const oldReceiptCcy = payment?.receipt_currency || null;
    const oldReceiptAmt = Number(payment?.receipt_amount || 0) || null;
    const newReceiptCcy = receiptEnabled && !clearReceipt
        ? (receiptCurrency || "").toUpperCase() : null;
    const newReceiptAmt = receiptEnabled && !clearReceipt
        ? Number(receiptAmount || 0) : null;
    const receiptChanged = clearReceipt
        ? !!(oldReceiptCcy || oldReceiptAmt)
        : (newReceiptCcy !== oldReceiptCcy
            || (newReceiptAmt || 0) !== (oldReceiptAmt || 0));
    const receiptSameAsInvoice = receiptEnabled && !clearReceipt
        && newReceiptCcy === invoiceCurrency;
    const receiptMissingAmount = receiptEnabled && !clearReceipt
        && !receiptSameAsInvoice && (newReceiptAmt === null || newReceiptAmt <= 0);

    // ── Live invariant probe (client-side mirror of the server guard) ─────
    const invariant = useMemo(() => {
        if (!invoice) return { ok: true };
        const effective = Number(invoice.effective_total ?? invoice.total_amount ?? 0);
        const paidAll = Number(invoice.paid_amount || 0);
        const tdsAll = Number(invoice.tds_amount || 0);
        const paidOther = +(paidAll - oldAmount).toFixed(2);
        const tdsOther = +(tdsAll - oldTds).toFixed(2);
        const settledAfter = +(paidOther + newAmount + tdsOther + newTds).toFixed(2);
        const overBy = +(settledAfter - effective).toFixed(2);
        return {
            ok: overBy <= 0.01,
            effective, paidOther, tdsOther, settledAfter, overBy,
        };
    }, [invoice, oldAmount, oldTds, newAmount, newTds]);

    const deltaCash = +(newAmount - oldAmount).toFixed(2);
    const deltaTds = +(newTds - oldTds).toFixed(2);
    const isNoop = Math.abs(deltaCash) < 0.01 && Math.abs(deltaTds) < 0.01 && !receiptChanged;
    const grossNew = +(newAmount + newTds).toFixed(2);

    const isDirty = !isNoop || !!reason || !!note;

    const localValid = (
        newAmount >= 0 && newTds >= 0
        && (newAmount + newTds > 0)
        && !!reason
        && note.trim().length >= NOTE_MIN
        && invariant.ok
        && !receiptSameAsInvoice
        && !receiptMissingAmount
    );

    const submit = async () => {
        if (!localValid) return;
        setBusy(true);
        try {
            const body = { amount: newAmount, tds_amount: newTds, reason, note: note.trim() };
            // Iter 61.2 — attach receipt-retrofit fields if user opted in.
            if (clearReceipt) {
                body.clear_receipt = true;
            } else if (receiptEnabled && newReceiptCcy && newReceiptAmt > 0) {
                body.receipt_currency = newReceiptCcy;
                body.receipt_amount = newReceiptAmt;
            }
            const { data } = await api.patch(
                `/billing/settlement/${payment.id}/correct`,
                body,
            );
            if (data?.is_noop) {
                toast.success("No-op correction recorded (audit-only — values unchanged)");
            } else {
                toast.success(
                    `Settlement corrected · Cash ${formatINR(newAmount)} + TDS ${formatINR(newTds)}`,
                );
            }
            onDone?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to correct settlement"));
        } finally {
            setBusy(false);
        }
    };

    if (!payment) return null;

    return (
        <FormDialog
            open={open}
            onOpenChange={(v) => !busy && onOpenChange(v)}
            isDirty={isDirty}
            contentClassName="max-w-xl"
            testId="correct-settlement-dialog"
            srTitle="Correct settlement (data-entry rescue)"
            srDescription="Primary-Admin-only correction of cash and TDS amounts on a recorded settlement. Fully audited."
            header={
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                        <Pencil size={18} weight="duotone" className="text-rose-600" />
                        Correct settlement
                        <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-rose-50 text-rose-700 border border-rose-200">
                            <ShieldCheck size={9} weight="fill" /> Primary Admin
                        </span>
                    </div>
                    <div className="text-xs text-slate-500">
                        Data-entry rescue — overwrites the cash + TDS values on this
                        settlement row. Identity (ref, date, mode, TDS stage) is
                        preserved. Every correction is audit-logged.
                    </div>
                </div>
            }
        >
            <div className="space-y-4 py-2">
                {/* Existing row context */}
                <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-xs text-slate-700 space-y-1" data-testid="correct-settlement-current">
                    <div className="flex items-center justify-between">
                        <span className="reedos-eyebrow text-[10px]">Current values</span>
                        <span className="text-[10px] text-slate-400 font-mono">{payment.id}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <span>Cash <b className="text-slate-900">{formatINR(oldAmount)}</b></span>
                        <span>·</span>
                        <span>TDS <b className="text-slate-900">{formatINR(oldTds)}</b></span>
                        <span>·</span>
                        <span>Gross <b className="text-slate-900">{formatINR(oldAmount + oldTds)}</b></span>
                    </div>
                    <div className="text-[10px] text-slate-500">
                        Mode: {payment.payment_mode} · Ref: {payment.reference_number || "—"} · TDS stage: {payment.tds_status || "—"}
                    </div>
                </div>

                {/* Framing banner */}
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
                    <Warning size={14} weight="fill" className="mt-0.5 text-amber-600 shrink-0" />
                    <span>
                        This action overwrites the recorded amounts. Use it only to fix
                        genuine data-entry mistakes — not for renegotiating, reversing,
                        or restructuring a settlement.
                    </span>
                </div>

                {/* Inputs — Cash + TDS side-by-side */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs font-semibold flex items-center gap-2">
                            New cash amount <span className="text-slate-400 text-[10px]">(₹)</span>
                        </Label>
                        <Input
                            type="number" min="0" step="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="mt-1 font-mono"
                            data-testid="correct-settlement-amount-input"
                        />
                        {Math.abs(deltaCash) >= 0.01 && (
                            <div className={`text-[10px] mt-1 ${deltaCash < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                                {deltaCash > 0 ? "+" : ""}{formatINR(deltaCash)} vs current
                            </div>
                        )}
                    </div>
                    <div>
                        <Label className="text-xs font-semibold flex items-center gap-2">
                            New TDS amount <span className="text-slate-400 text-[10px]">(₹)</span>
                        </Label>
                        <Input
                            type="number" min="0" step="0.01"
                            value={tdsAmount}
                            onChange={(e) => setTdsAmount(e.target.value)}
                            className="mt-1 font-mono"
                            data-testid="correct-settlement-tds-input"
                        />
                        {Math.abs(deltaTds) >= 0.01 && (
                            <div className={`text-[10px] mt-1 ${deltaTds < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                                {deltaTds > 0 ? "+" : ""}{formatINR(deltaTds)} vs current
                            </div>
                        )}
                    </div>
                </div>

                {/* Live preview row */}
                <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs space-y-1" data-testid="correct-settlement-preview">
                    <div className="flex items-center justify-between text-slate-600">
                        <span className="reedos-eyebrow text-[10px]">Preview</span>
                        <span className="text-[10px] text-slate-400 font-mono inline-flex items-center gap-1">
                            <ArrowRight size={10} /> after correction
                        </span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-800">
                        <span>Cash <b className="font-mono">{formatINR(newAmount)}</b></span>
                        <span>+ TDS <b className="font-mono">{formatINR(newTds)}</b></span>
                        <span>=</span>
                        <span>Gross <b className="font-mono">{formatINR(grossNew)}</b></span>
                    </div>
                    {!invariant.ok && (
                        <div className="text-rose-700 text-[11px] mt-1" data-testid="correct-settlement-over-settle-warning">
                            Over-settles the invoice by {formatINR(invariant.overBy)} —
                            other settlements on this invoice already contribute Cash {formatINR(invariant.paidOther)} + TDS {formatINR(invariant.tdsOther)}.
                            Reduce the new values.
                        </div>
                    )}
                    {invariant.ok && newAmount === 0 && newTds === 0 && (
                        <div className="text-rose-700 text-[11px] mt-1">
                            Composite must be positive — set either cash or TDS to a non-zero value.
                        </div>
                    )}
                </div>

                {/* Iter 61.2 — Cross-currency receipt retrofit.
                    Lets the Primary Admin attach (or edit, or clear) the
                    actual bank-receipt currency/amount on an existing
                    settlement row — for cases where the operator forgot to
                    tick the Cross-currency toggle when first recording. */}
                <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2.5">
                    <label className="flex items-start gap-2 cursor-pointer text-[12px] text-slate-800">
                        <input
                            type="checkbox"
                            checked={receiptEnabled}
                            onChange={(e) => {
                                const on = e.target.checked;
                                setReceiptEnabled(on);
                                setClearReceipt(false);
                                if (on && !payment?.receipt_currency) {
                                    const firstAlt = ["INR", "USD", "AED", "GBP", "EUR"]
                                        .find((c) => c !== invoiceCurrency) || "INR";
                                    setReceiptCurrency(firstAlt);
                                }
                            }}
                            className="mt-0.5 accent-indigo-600"
                            data-testid="correct-settlement-receipt-toggle"
                        />
                        <span>
                            <b>Attach / edit cross-currency receipt info</b>
                            <span className="block text-[10px] text-slate-500 mt-0.5">
                                Invoice is in <span className="font-mono font-semibold">{invoiceCurrency}</span>.
                                Useful when the actual bank receipt was in a different currency than the invoice.
                                Does NOT affect outstanding / aging — ledger reconciliation only.
                            </span>
                        </span>
                    </label>
                    {receiptEnabled && (
                        <div className="mt-2.5 grid grid-cols-2 gap-3" data-testid="correct-settlement-receipt-fields">
                            <div>
                                <Label className="text-[11px] font-semibold">Actual receipt currency</Label>
                                <select
                                    value={receiptCurrency}
                                    onChange={(e) => setReceiptCurrency(e.target.value)}
                                    className="mt-1 w-full h-9 px-2 text-[12px] border border-slate-200 rounded-md bg-white"
                                    data-testid="correct-settlement-receipt-currency-select"
                                >
                                    {["INR", "USD", "AED", "GBP", "EUR"].filter((c) => c !== invoiceCurrency).map((c) => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <Label className="text-[11px] font-semibold">Actual receipt amount</Label>
                                <Input
                                    type="number" step="0.01" min="0"
                                    value={receiptAmount}
                                    onChange={(e) => setReceiptAmount(e.target.value)}
                                    placeholder="e.g. 577150"
                                    className="mt-1 h-9 font-mono text-right"
                                    data-testid="correct-settlement-receipt-amount-input"
                                />
                            </div>
                            {oldReceiptCcy && oldReceiptAmt && (
                                <div className="col-span-2 text-[10.5px] text-slate-600 bg-white border border-slate-200 rounded px-2 py-1.5">
                                    Currently on row: <span className="font-mono font-semibold">{oldReceiptCcy} {oldReceiptAmt.toLocaleString("en-IN")}</span>
                                </div>
                            )}
                            {!oldReceiptCcy && (
                                <div className="col-span-2 text-[10.5px] text-slate-600 bg-white border border-slate-200 rounded px-2 py-1.5">
                                    This row currently has <b>no</b> receipt info — saving will attach it.
                                </div>
                            )}
                            {receiptMissingAmount && (
                                <div className="col-span-2 text-[11px] text-rose-700 font-semibold" data-testid="correct-settlement-receipt-amount-error">
                                    Receipt amount is required and must be {">"} 0.
                                </div>
                            )}
                            {receiptSameAsInvoice && (
                                <div className="col-span-2 text-[11px] text-rose-700 font-semibold" data-testid="correct-settlement-receipt-same-error">
                                    Receipt currency must differ from the invoice currency.
                                </div>
                            )}
                        </div>
                    )}
                    {oldReceiptCcy && !receiptEnabled && (
                        <label className="flex items-start gap-2 cursor-pointer text-[11px] text-rose-700 mt-2">
                            <input
                                type="checkbox"
                                checked={clearReceipt}
                                onChange={(e) => setClearReceipt(e.target.checked)}
                                className="mt-0.5 accent-rose-600"
                                data-testid="correct-settlement-clear-receipt"
                            />
                            <span>
                                <b>Clear existing receipt info</b> <span className="text-slate-500">({oldReceiptCcy} {oldReceiptAmt?.toLocaleString("en-IN")})</span> — use only if it was mistakenly attached.
                            </span>
                        </label>
                    )}
                </div>

                {/* Reason + note */}
                <div>
                    <Label className="text-xs font-semibold">Correction reason *</Label>
                    <select
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400/40"
                        data-testid="correct-settlement-reason-select"
                    >
                        <option value="">— Pick a reason —</option>
                        {REASONS.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <Label className="text-xs font-semibold">Operator note *</Label>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="What was wrongly entered, what's the right value, supporting reference (bank stmt / Form 16A / ticket)…"
                        rows={3}
                        maxLength={1000}
                        className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400/40"
                        data-testid="correct-settlement-note-input"
                    />
                    <div className="text-[10px] text-slate-400 mt-1">
                        {note.length} / 1000 · min {NOTE_MIN} characters
                    </div>
                </div>
            </div>
            <DialogFooter className="border-t border-slate-100 -mx-6 px-6 pt-4 mt-2">
                <FormDialogCancel disabled={busy} testId="correct-settlement-cancel-btn" />
                <Button
                    onClick={submit}
                    disabled={busy || !localValid}
                    className="bg-rose-600 hover:bg-rose-500 text-white gap-1.5"
                    data-testid="correct-settlement-confirm-btn"
                >
                    <Pencil size={14} />
                    {busy ? "Applying…" : isNoop ? "Record no-op audit" : "Apply correction"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}
